import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { NinetailedPreviewPlugin } from '@ninetailed/experience.js-plugin-preview';
import { NinetailedInsightsPlugin } from '@ninetailed/experience.js-plugin-insights';
import { useLocation } from '@thd-olt-component-react/router';
import { NinetailedProvider } from '@ninetailed/experience.js-react';
import { fetchNinetailedComponentExperiencesFormatted } from '../../utils/ninetailed';
import { GuestHomepage } from './GuestHomepage';

export const GuestHomepageContainer = ({ slug, thdSEOBotDetection }) => {

  const { isCustomerIdentified = false } = useThdCustomer();
  const store = useStore();
  const { isLocalized } = store;
  const { search: querystring, pathname } = useLocation();

  const [formattedExperiences, setFormattedExperiences] = useState(null); // null means "not loaded"
  const pathWithQueryParams = `${pathname}${querystring}`;
  const isPreviewPage = pathWithQueryParams.indexOf('render*') > -1
    || pathWithQueryParams.indexOf('contentPV') > -1
    || pathWithQueryParams.indexOf('bassPV') > -1;

  const CONTENTFUL_SPACE_ID = useConfigService('contentful.space.id.martech') || '';
  const CONTENTFUL_PREVIEW_TOKEN = useConfigService('contentful.tokens.martech.preview') || '';
  const NINETAILED_CLIENT_ID = useConfigService('contentful.ninetailed.client.id') || '';
  const CONTENTFUL_ENV = useConfigService('contentful.env') || '';

  useEffect(() => {
    if (isPreviewPage && slug) {
      fetchNinetailedComponentExperiencesFormatted(
        slug,
        CONTENTFUL_PREVIEW_TOKEN,
        CONTENTFUL_SPACE_ID,
        CONTENTFUL_ENV
      )
        .then(setFormattedExperiences)
        .catch((err) => {
          setFormattedExperiences([]); // fallback to empty array
        });
    } else {
      setFormattedExperiences([]); // fallback if no slug or not in preview mode
    }
  }, [slug, isPreviewPage]);

  // Wait for data to load before rendering anything
  if (isPreviewPage && (formattedExperiences === null || formattedExperiences.audience === null)) {
    return null; // or return a loader/spinner if you want
  }

  const plugins = isPreviewPage
    ? [
      new NinetailedPreviewPlugin({
        experiences: formattedExperiences,
        audiences: formattedExperiences.map((exp) => exp.audience).filter(Boolean),
        onOpenExperienceEditor: (experience) => {},
        onOpenAudienceEditor: (audience) => {},
        ui: { opener: { hide: false } }
      }), new NinetailedInsightsPlugin()
    ] : [new NinetailedInsightsPlugin()];

  const hpDefaultVariables = {
    isBrandPricingPolicyCompliant: isLocalized ? isCustomerIdentified : false
  };

  return (
    <ErrorBoundary name="my-homepage-content">
      <NinetailedProvider
        clientId={NINETAILED_CLIENT_ID}
        environment="development"
        plugins={plugins}
      >
        <QueryProvider
          cacheKey="consumer-homepage"
          defaultVariables={hpDefaultVariables}
        >
          <GuestHomepage slug={slug} thdSEOBotDetection={thdSEOBotDetection} />
        </QueryProvider>
      </NinetailedProvider>
    </ErrorBoundary>
  );
};

GuestHomepageContainer.propTypes = {
  slug: PropTypes.string,
  thdSEOBotDetection: PropTypes.bool
};

GuestHomepageContainer.defaultProps = {
  slug: undefined,
  thdSEOBotDetection: false
};
